import { getToken } from 'firebaseClient';
import {
  Environment,
  FeatureName,
} from 'types/ez4cast';
import { FEATURE_TO_ENDPOINT } from 'utils/ez4castEndpointList';

/**
 * Base URLs for different environments
 */
const BASE_URLS = {
  devUS: 'http://127.0.0.1:5001/ez4cast/us-central1/',
  prod: 'https://us-central1-ez4cast.cloudfunctions.net/',
  europeProd: 'https://europe-west1-ez4cast.cloudfunctions.net/',
} as const;

const urlCache = new Map<string, string>();

/**
 * Get the base URL for the current environment
 * @param forceDev - If true, returns dev URL regardless of environment variable
 * @returns The base URL for the current environment
 */
const getBaseUrlForEnv = (forceDev: boolean = false): string => {
  if (forceDev) {
    return BASE_URLS.devUS;
  }

  const environment = process.env.NEXT_PUBLIC_EZ4CAST_ENV as Environment;

  if (!environment) {
    console.warn('EZ4CAST_ENV environment variable not set, defaulting to prod');
    return BASE_URLS.prod;
  }

  return environment === 'dev' ? BASE_URLS.devUS : BASE_URLS.prod;
};

/**
 * Get the API URL for a specific feature
 * Uses caching for better performance
 * @param feature - The feature name to get the URL for
 * @param forceDev - If true, forces the use of development URL
 * @returns The complete API URL
 *
 * @example
 * const url = getApiUrl('inspirationGet');
 * const devUrl = getApiUrl('inspirationGet', true);
 */
export const getApiUrl = (
  feature: FeatureName,
  forceDev: boolean = false
): string => {
  const cacheKey = `${feature}:${forceDev ? 'dev' : process.env.NEXT_PUBLIC_EZ4CAST_ENV || 'prod'
    }`;
  const cachedUrl = urlCache.get(cacheKey);

  if (cachedUrl) {
    return cachedUrl;
  }

  const baseUrl = getBaseUrlForEnv(forceDev);
  const endpoint = FEATURE_TO_ENDPOINT[feature];
  const url = `${baseUrl}${endpoint}`;

  urlCache.set(cacheKey, url);
  return url;
};

/**
 * Get the endpoint path for a specific feature
 * @param feature - The feature name to get the endpoint for
 * @returns The endpoint path
 *
 * @example
 * const endpoint = getEndpoint('inspirationGet');
 */
export const getEndpoint = (feature: FeatureName): string => {
  return FEATURE_TO_ENDPOINT[feature];
};

/**
 * Check if a feature exists in the configuration
 * Uses Set for O(1) lookup performance
 * @param feature - The feature name to check
 * @returns boolean indicating if the feature exists
 *
 * @example
 * const exists = hasFeature('inspirationGet'); // true
 * const notExists = hasFeature('nonExistentFeature'); // false
 */
const featureSet = new Set(Object.keys(FEATURE_TO_ENDPOINT));
export const hasFeature = (feature: string): feature is FeatureName => {
  return featureSet.has(feature as FeatureName);
};

/**
 * Get all available features
 * Returns readonly array for better performance and immutability
 * @returns Array of all available feature names
 *
 * @example
 * const features = getAllFeatures();
 */
export const getAllFeatures = (): readonly FeatureName[] => {
  return Object.keys(FEATURE_TO_ENDPOINT) as FeatureName[];
};

const featureUrlsCache = new Map<
  FeatureName,
  Readonly<Record<Environment, string>>
>();

/**
 * Get all URLs for a specific feature
 * Uses caching for better performance
 * @param feature - The feature name to get URLs for
 * @returns Object containing URLs for all environments
 *
 * @example
 * const urls = getFeatureUrls('inspirationGet');
 */
export const getFeatureUrls = (
  feature: FeatureName
): Readonly<Record<Environment, string>> => {
  const cached = featureUrlsCache.get(feature);
  if (cached) {
    return cached;
  }

  const urls = {
    dev: getApiUrl(feature, true),
    prod: getApiUrl(feature, false),
  } as const;

  featureUrlsCache.set(feature, urls);
  return urls;
};

/**
 * Get the base URL for the current environment
 * @param forceDev - If true, forces the use of development URL
 * @returns The base URL for the current environment
 *
 * @example
 * const baseUrl = getBaseUrl();
 * const devBaseUrl = getBaseUrl(true);
 */
export const getBaseUrl = (forceDev: boolean = false): string => {
  return getBaseUrlForEnv(forceDev);
};

/**
 * Clear all caches
 * Useful for testing or when URLs need to be refreshed
 *
 * @example
 * clearCaches();
 */
export const clearCaches = (): void => {
  urlCache.clear();
  featureUrlsCache.clear();
};

export const ez4castBaseUrl = BASE_URLS;

async function getAuthHeaders(session: any, source: string) {
  const token = await getToken(session, source);
  const tokenuserid = session.user.uid;

  if (!token || !tokenuserid) {
    throw new Error('No token or tokenuserid found');
  }

  return {
    Authorization: `Bearer ${token}`,
    tokenuserid,
  };
}

